// src/pages/produtos/produtos/ModalHorariosProduto.js
import React, { useEffect, useMemo, useState } from 'react';
import { buscarHorariosProduto, salvarHorariosProduto } from '../../../services/produtos/ProdutoHorarioService';
import { useAlert } from '../../../contexts/geral/AlertContext';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import Input from '../../../components/input/Input';
import './ProdutoSearchDelivery.css';

const diasSemana = [
  { label: 'D', nome: 'Domingo' },
  { label: 'S', nome: 'Segunda' },
  { label: 'T', nome: 'Terça' },
  { label: 'Q', nome: 'Quarta' },
  { label: 'Q', nome: 'Quinta' },
  { label: 'S', nome: 'Sexta' },
  { label: 'S', nome: 'Sábado' }
];

const horaStringParaInput = (hora) => {
  if (!hora || typeof hora !== 'string') return '';
  return hora.slice(0, 5);
};

const ModalHorariosProduto = ({ show, onHide, produto }) => {
  const { showAlert } = useAlert();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registrosAtuais, setRegistrosAtuais] = useState([]);
  const [diasSelecionados, setDiasSelecionados] = useState([]);
  const [horarios, setHorarios] = useState({});
  const [erros, setErros] = useState({});

  const produtoId = produto?.id;

  const tituloProduto = useMemo(() => {
    if (!produto) return '';
    return `Produto ${produto.id} - ${produto.descricao || ''}`;
  }, [produto]);

  useEffect(() => {
    if (!show || !produtoId) return;

    const carregar = async () => {
      setLoading(true);
      setErros({});

      try {
        const data = await buscarHorariosProduto(produtoId);
        const lista = Array.isArray(data) ? data : [];
        const estrutura = {};
        const dias = [];

        lista.forEach((item) => {
          const dia = Number(item.diaSemana);
          dias.push(dia);

          estrutura[dia] = (item.periodos || []).map((p) => ({
            inicio: horaStringParaInput(p.horaInicio),
            fim: horaStringParaInput(p.horaFim)
          }));

          if (!estrutura[dia] || estrutura[dia].length === 0) {
            estrutura[dia] = [{ inicio: '', fim: '' }];
          }
        });

        setRegistrosAtuais(lista);
        setDiasSelecionados(dias.sort((a, b) => a - b));
        setHorarios(estrutura);
      } catch (error) {
        setRegistrosAtuais([]);
        setDiasSelecionados([]);
        setHorarios({});
        showAlert('Erro ao carregar horários do produto.', 'danger');
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [show, produtoId, showAlert]);

  if (!show) return null;

  const toggleDia = (dia) => {
    setErros((prev) => {
      const novo = { ...prev };
      delete novo[dia];
      return novo;
    });

    if (diasSelecionados.includes(dia)) {
      setDiasSelecionados((prev) => prev.filter((d) => d !== dia));

      setHorarios((prev) => {
        const novo = { ...prev };
        delete novo[dia];
        return novo;
      });
      return;
    }

    setDiasSelecionados((prev) => [...prev, dia].sort((a, b) => a - b));
    setHorarios((prev) => ({
      ...prev,
      [dia]: [{ inicio: '', fim: '' }]
    }));
  };

  const alterarHorario = (dia, idx, campo, valor) => {
    setHorarios((prev) => {
      const lista = [...(prev[dia] || [])];
      lista[idx] = { ...lista[idx], [campo]: valor };
      return { ...prev, [dia]: lista };
    });

    setErros((prev) => {
      const novo = { ...prev };
      delete novo[`${dia}-${idx}-inicio`];
      delete novo[`${dia}-${idx}-fim`];
      delete novo[`${dia}-${idx}-intervalo`];
      return novo;
    });
  };

  const adicionarPeriodo = (dia) => {
    setHorarios((prev) => ({
      ...prev,
      [dia]: [...(prev[dia] || []), { inicio: '', fim: '' }]
    }));
  };

  const removerPeriodo = (dia, idx) => {
    setHorarios((prev) => {
      const lista = [...(prev[dia] || [])];
      lista.splice(idx, 1);
      return { ...prev, [dia]: lista };
    });
  };

  const handleSalvar = async () => {
    if (!produtoId) return;

    const novosErros = {};
    const payload = diasSelecionados.map((dia) => {
      const periodosValidos = [];

      (horarios[dia] || []).forEach((periodo, idx) => {
        const inicio = periodo?.inicio || '';
        const fim = periodo?.fim || '';
        const temInicio = !!inicio;
        const temFim = !!fim;

        if (!temInicio && !temFim) return;

        if (temInicio && !temFim) {
          novosErros[`${dia}-${idx}-fim`] = 'Informe o fim';
          return;
        }

        if (!temInicio && temFim) {
          novosErros[`${dia}-${idx}-inicio`] = 'Informe o início';
          return;
        }

        if (inicio >= fim) {
          novosErros[`${dia}-${idx}-intervalo`] = 'O início deve ser anterior ao fim';
          return;
        }

        periodosValidos.push({
          horaInicio: `${inicio}:00`,
          horaFim: `${fim}:00`
        });
      });

      return {
        produtoId,
        diaSemana: dia,
        periodos: periodosValidos
      };
    });

    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) return;

    setSaving(true);
    try {
      await salvarHorariosProduto(registrosAtuais, payload);

      showAlert('Horários do produto salvos com sucesso.');
      onHide();
    } catch (error) {
      showAlert('Erro ao salvar horários do produto.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer show={show} onHide={saving ? undefined : onHide} largura="760px" variant="cadastro">
          <ModalHeader title="Horários do produto" onHide={onHide} closeDisabled={saving} />
          <ModalBody>
            <div className="produto-horario-subtitle">{tituloProduto}</div>

            <div className="produto-horario-label">Dias da semana *</div>

            <div className="produto-dias-semana-container">
              {diasSemana.map((dia, index) => (
                <button
                  key={index}
                  type="button"
                  className={`produto-dia-badge ${diasSelecionados.includes(index) ? 'ativo' : ''}`}
                  aria-label={dia.nome}
                  aria-pressed={diasSelecionados.includes(index)}
                  disabled={loading || saving}
                  onClick={() => toggleDia(index)}
                >
                  {dia.label}
                </button>
              ))}
            </div>

            <div className="produto-horario-observacao">
              Se informar apenas o dia, sem horário, o produto fica disponível o dia inteiro.
            </div>

            {loading ? (
              <div className="text-muted" role="status">Carregando horários...</div>
            ) : (
              <div className="produto-horarios-detalhe">
                {diasSelecionados.map((dia) => (
                  <div key={dia} className="produto-dia-horario-bloco">
                    <div className="produto-dia-nome">{diasSemana[dia].nome}:</div>

                    <div className="produto-periodos-container">
                      {(horarios[dia] || []).map((periodo, idx) => (
                        <div key={idx} className="produto-periodo-row">
                          <Input
                            label="Início"
                            size={120}
                            disabled={saving}
                            type="time"
                            value={periodo.inicio}
                            msgErro={erros[`${dia}-${idx}-inicio`] || erros[`${dia}-${idx}-intervalo`]}
                            onChange={(e) => alterarHorario(dia, idx, 'inicio', e.target.value)}
                          />

                          <Input
                            label="Fim"
                            size={120}
                            disabled={saving}
                            type="time"
                            value={periodo.fim}
                            msgErro={erros[`${dia}-${idx}-fim`]}
                            onChange={(e) => alterarHorario(dia, idx, 'fim', e.target.value)}
                          />

                          <button
                            type="button"
                            className="btn-remove-periodo"
                            aria-label={`Remover período de ${diasSemana[dia].nome}`}
                            disabled={saving}
                            onClick={() => removerPeriodo(dia, idx)}
                          >
                            -
                          </button>

                          <button
                            type="button"
                            className="btn-add-periodo"
                            aria-label={`Adicionar período de ${diasSemana[dia].nome}`}
                            disabled={saving}
                            onClick={() => adicionarPeriodo(dia)}
                          >
                            +
                          </button>

                        </div>
                      ))}

                      {(horarios[dia] || []).length === 0 && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => adicionarPeriodo(dia)}
                        >
                          + Adicionar horário
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onHide} disabled={saving}>
              Fechar
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSalvar} disabled={saving || loading}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </ModalFooter>
    </ModalContainer>
  );
};

export default ModalHorariosProduto;
